import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext, DragEndEvent, DragOverlay, DragStartEvent, PointerSensor, useDraggable, useDroppable, useSensor, useSensors,
} from '@dnd-kit/core';
import { apiGet, apiPatch } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Stage, tierColor } from '../lib/types';
import { Badge, EmptyState, Loading } from '../components/ui';

interface Card {
  id: number;
  businessName: string | null;
  contactName: string | null;
  companyName: string | null;
  stageId: string | null;
  tier: string | null;
  dealValue: number | null;
  ownerName: string | null;
  ownerUserId: string | null;
}
interface Board { stages: Stage[]; contacts: Card[]; canSeeAll: boolean }

export default function Pipeline() {
  const [owner, setOwner] = useState('');
  const { data, loading, error, reload, setData } = useAsync(
    () => apiGet<Board>(`/api/pipeline${owner ? `?owner=${owner}` : ''}`),
    [owner]
  );
  const members = useAsync(() => apiGet<{ members: any[] }>('/api/group/members'), []);
  const [activeId, setActiveId] = useState<number | null>(null);
  const navigate = useNavigate();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const byStage = useMemo(() => {
    const m: Record<string, Card[]> = {};
    (data?.stages || []).forEach((s) => (m[s.id] = []));
    (data?.contacts || []).forEach((c) => { if (c.stageId && m[c.stageId]) m[c.stageId].push(c); });
    return m;
  }, [data]);

  if (loading) return <Loading label="Loading pipeline…" />;
  if (error) return <div className="text-danger">{error}</div>;
  if (!data) return null;

  const activeCard = data.contacts.find((c) => c.id === activeId) || null;

  function onDragStart(e: DragStartEvent) { setActiveId(Number(e.active.id)); }
  async function onDragEnd(e: DragEndEvent) {
    setActiveId(null);
    const cardId = Number(e.active.id);
    const overStage = e.over?.id ? String(e.over.id) : null;
    if (!overStage || !data) return;
    const card = data.contacts.find((c) => c.id === cardId);
    if (!card || card.stageId === overStage) return;
    // Optimistic move.
    setData({ ...data, contacts: data.contacts.map((c) => (c.id === cardId ? { ...c, stageId: overStage } : c)) });
    try {
      await apiPatch(`/api/contacts/${cardId}`, { stageId: overStage });
    } catch {
      reload();
    }
  }

  const hasContacts = data.contacts.length > 0;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="h-display text-3xl text-soft">Pipeline</h1>
          <p className="mt-1 text-sm text-muted">Drag cards across stages. Every move logs itself to the timeline.</p>
        </div>
        {data.canSeeAll && (
          <select value={owner} onChange={(e) => setOwner(e.target.value)}
            className="rounded-lg border border-line bg-surface-3 px-3 py-2 text-sm text-soft outline-none focus:border-power">
            <option value="">Everyone</option>
            {(members.data?.members || []).map((m) => <option key={m.userId} value={m.userId}>{m.fullName || m.email}</option>)}
          </select>
        )}
      </div>

      {!hasContacts ? (
        <EmptyState title="Your board is empty" body="Add contacts and they'll appear here in the first stage. Drag them across as deals progress." />
      ) : (
        <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <div className="flex gap-3 overflow-x-auto pb-4">
            {data.stages.map((s) => (
              <Column key={s.id} stage={s} cards={byStage[s.id] || []} onOpen={(id) => navigate(`/contacts/${id}`)} />
            ))}
          </div>
          <DragOverlay>{activeCard ? <CardView card={activeCard} dragging /> : null}</DragOverlay>
        </DndContext>
      )}
    </div>
  );
}

function Column({ stage, cards, onOpen }: { stage: Stage; cards: Card[]; onOpen: (id: number) => void }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage.id });
  const value = cards.reduce((a, c) => a + (c.dealValue || 0), 0);
  return (
    <div ref={setNodeRef}
      className={`flex w-72 shrink-0 flex-col rounded-2xl border bg-surface/40 ${isOver ? 'border-power' : 'border-line'}`}>
      <div className="flex items-center justify-between px-3 py-2.5">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: stage.color || '#5B8FF0' }} />
          <span className="font-head text-sm font-semibold text-soft">{stage.name}</span>
          <span className="text-xs text-muted">{cards.length}</span>
        </div>
        {value > 0 && <span className="text-xs text-muted">${value.toLocaleString()}</span>}
      </div>
      <div className="flex-1 space-y-2 px-2 pb-3">
        {cards.map((c) => <DraggableCard key={c.id} card={c} onOpen={onOpen} />)}
        {cards.length === 0 && <div className="rounded-xl border border-dashed border-line py-6 text-center text-xs text-muted">Drop here</div>}
      </div>
    </div>
  );
}

function DraggableCard({ card, onOpen }: { card: Card; onOpen: (id: number) => void }) {
  const { setNodeRef, listeners, attributes, isDragging } = useDraggable({ id: card.id });
  return (
    <div ref={setNodeRef} {...listeners} {...attributes}
      onClick={() => onOpen(card.id)}
      className={`cursor-grab ${isDragging ? 'opacity-30' : ''}`}>
      <CardView card={card} />
    </div>
  );
}

function CardView({ card, dragging }: { card: Card; dragging?: boolean }) {
  return (
    <div className={`card px-3 py-2.5 ${dragging ? 'shadow-pop' : ''}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate font-head text-sm font-semibold text-soft">{card.businessName || card.contactName}</div>
          {card.companyName && card.contactName && <div className="truncate text-xs text-muted">{card.companyName}</div>}
        </div>
        {card.tier && <Badge className={`border-line ${tierColor(card.tier)}`}>{card.tier}</Badge>}
      </div>
      <div className="mt-1.5 flex items-center justify-between text-xs text-muted">
        <span className="truncate">{card.ownerName || '—'}</span>
        {card.dealValue != null && <span>${Number(card.dealValue).toLocaleString()}</span>}
      </div>
    </div>
  );
}
